import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { Card, Img, Text } from '../../Atoms';
import { OrganismFormDocument, DescriptionRenderedSendDocument} from '../../Organisms';
import success from '../../assets/img/doc-success.png';
import PageTitle from '../../Components/Dashboard/PageTitle';
import PropTypes from 'prop-types';

/**
 * Génère les cartes pour la sélection du type de document
 * @param {Array} items - Les éléments à afficher
 * @param {Function} onSelect - Fonction de sélection
 * @returns {JSX.Element[]} - Liste des éléments à rendre
 */
const renderDocumentCards = (items, onSelect) => {
  return items?.map((el, index) => (
    <Col
      key={el.id || `doc-type-${index}`} // Utilisation d'un ID unique au lieu de l'index
      md={items.length % 2 === 1 && index + 1 === items.length ? 12 : 6}
      xs={12}
      className={getCardColumnClassName(items, index)}
    >
      <Card
        size="small"
        text={el.name}
        className="card-type-doc"
        onClick={() => onSelect(el)}
      />
    </Col>
  )) || null;
};

/**
 * Détermine la classe CSS pour la colonne de carte
 * @param {Array} items - La liste complète des éléments
 * @param {number} index - L'index actuel
 * @returns {string} - La classe CSS à appliquer
 */
const getCardColumnClassName = (items, index) => {
  if (items.length % 2 === 1 && index + 1 === items.length) {
    return 'container-center';
  }
  return index % 2 === 0 ? 'container-left-card-doc' : 'container-right-card-doc';
};

/**
 * Détermine si la description de l'envoi de document doit être affichée
 * @param {Object} state - L'état du composant
 * @returns {boolean} - Indique si la description doit être affichée
 */
const shouldShowDescription = (state) => {
  const { type, category, stepMedicine } = state;
  const isMedecineDouce = category?.toLowerCase() === 'médecine douce';
  const isFacture = type?.name?.toLowerCase() === 'facture';
  const isMobile = window.innerWidth < 768;

  if (!isMedecineDouce) return true;
  if (isMobile && isFacture && isMedecineDouce && stepMedicine === 1) return true;
  return !isMobile || !isMedecineDouce;
};

/**
 * Filtrer les éléments par catégorie parente
 * @param {Array<{parentCategoryId: string | null}>} items - Les éléments à filtrer
 * @returns {Array<{parentCategoryId: string | null}>} - Les éléments filtrés
 */
const filterParentCategories = (items) => {
  return items?.filter(el => !el.parentCategoryId) || [];
};

/**
 * Composant Template pour l'envoi de documents
 * Gère le flux d'envoi de documents en plusieurs étapes
 */
function TemplateSendDocument(props) {
  const {
    state, onReturn, selectElement, selectCategory, selectFile,
    onSubmit, onChange, onChangeMedicine, medicineStep, openNewMutual,
    onChangeMutual, onChangeSelect, onSelectSeuil
  } = props;

  const { step, type, data, categoryList } = state;
  const category = type?.name;

  // Rendu de l'étape 1 - Sélection du type principal de document
  const renderStep1 = () => {
    const parentCategories = filterParentCategories(data);

    return (
      <div className="content-template-document">
        <div className="container-text-type-doc">
          <Text
            text="Quel type de document souhaitez-vous nous envoyer ?"
            className="text-type-doc"
            type="h8"
          />
        </div>
        <Row>
          {parentCategories.map((el, index) => (
            <Col
              key={el.id || `parent-category-${index}`} // Utilisation d'un ID unique
              md={6}
              xs={12}
              className={index % 2 === 0 ? 'container-left-card-doc' : 'container-right-card-doc'}
            >
              <Card
                size="small"
                text={el.name}
                className="card-type-doc"
                onClick={() => selectElement(el)}
              />
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // Rendu de l'étape 2 - Sélection de la sous-catégorie de document
  const renderStep2 = () => {
    const hasCompactLayout = categoryList?.length <= 5;

    return (
      <div className="content-template-document">
        <div className="container-text-type-doc">
          <Text
            text="Merci de bien vouloir préciser votre type de document"
            className="text-type-doc"
            type="h8"
          />
        </div>
        <Row className={hasCompactLayout ? 'd-flex justify-content-center' : ''}>
          {hasCompactLayout ? (
            <div>
              {categoryList?.map((el, index) => (
                <Col key={el.id || `subcategory-${index}`} md={6} xs={12}>
                  <Card
                    size="small"
                    text={el.name}
                    className="card-type-doc"
                    onClick={() => selectCategory(el)}
                  />
                </Col>
              ))}
            </div>
          ) : renderDocumentCards(categoryList, selectCategory)}
        </Row>
      </div>
    );
  };

  // Rendu de l'étape 3 - Formulaire d'envoi de document
  const renderStep3 = () => (
    <div>
      <Row>
        <Col xs={12} md={7}>
          <div className="container-description-condition-send-document">
            {shouldShowDescription(state) && (
              <DescriptionRenderedSendDocument
                selectedSeuil={state.selectedSeuil}
                onSelectSeuil={onSelectSeuil}
                type={state.type}
                categoryList={state.data}
                category={state.category}
              />
            )}
          </div>
        </Col>
        <Col xs={12} md={5} className="container-col-send-doc">
          <OrganismFormDocument
            selectFile={selectFile}
            description={state.description}
            onSubmit={onSubmit}
            onChange={onChange}
            type={state.type}
            category={state.category}
            onChangeMedicine={onChangeMedicine}
            medicine={state.medicine}
            medicineStep={medicineStep}
            openNewMutual={openNewMutual}
            stepMedicine={state.stepMedicine}
            showNewMutual={state.showNewMutual}
            mutual={state.mutual}
            onChangeMutual={onChangeMutual}
            onChangeSelect={onChangeSelect}
            remboursement={state.remboursement}
            isUploadValid={state.isUploadValid}
            files={state.files}
            isDescriptionValid={state.isDescriptionValid}
            seuil={state.selectedSeuil}
          />
        </Col>
      </Row>
    </div>
  );

  // Rendu de l'étape 4 - Confirmation d'envoi
  const renderStep4 = () => (
    <div className="container-success">
      <Text
        text="Votre document a bien été envoyé."
        type="h4"
        className="doc-success-msg text-default-size"
      />
      <div className="container-center">
        <Img src={success} className="img-success-document object-fit" />
      </div>
    </div>
  );

  // Rendu du contenu en fonction de l'étape
  const renderContent = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep4();
    }
  };

  return (
    <div className="template-container template-send-document template-scroll">
      <PageTitle
        titleText={'Envoyer un document'}
        onTitleClick={onReturn}
      />

      {category && step === 2 && (
        <div style={{ paddingTop: 25, paddingLeft: 50 }}>
          <Text text={category} className="text-type-doc" type="h8"></Text>
        </div>
      )}

      {renderContent()}
    </div>
  );
}

TemplateSendDocument.propTypes = {
  state: PropTypes.shape({
    step: PropTypes.number.isRequired,
    type: PropTypes.shape({
      name: PropTypes.string
    }),
    categoryList: PropTypes.array,
    data: PropTypes.array,
    category: PropTypes.string,
    selectedSeuil: PropTypes.number,
    description: PropTypes.string,
    stepMedicine: PropTypes.number,
    showNewMutual: PropTypes.bool,
    mutual: PropTypes.object,
    remboursement: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isUploadValid: PropTypes.bool,
    files: PropTypes.array,
    isDescriptionValid: PropTypes.bool,
    medicine: PropTypes.object
  }).isRequired,
  onReturn: PropTypes.func.isRequired,
  selectElement: PropTypes.func.isRequired,
  selectCategory: PropTypes.func.isRequired,
  selectFile: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onChangeMedicine: PropTypes.func.isRequired,
  medicineStep: PropTypes.func.isRequired,
  openNewMutual: PropTypes.func.isRequired,
  onChangeMutual: PropTypes.func.isRequired,
  onChangeSelect: PropTypes.func.isRequired,
  onSelectSeuil: PropTypes.func.isRequired
};

export default TemplateSendDocument;
